import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Copy, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "destructive",
  loading = false,
  confirmPhrase,
  children,
  onConfirm,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "destructive" | "default";
  loading?: boolean;
  // When set, the user must type this exact phrase (e.g. the resource name)
  // before Confirm enables — a guard against accidental destructive actions.
  confirmPhrase?: string;
  // Optional extra content (e.g. a teardown preflight list) rendered between
  // the description and the confirm-phrase input.
  children?: React.ReactNode;
  onConfirm: () => void;
  // For dialogs opened without a trigger: put focus back where it came from
  // (call preventDefault after focusing).
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const [typed, setTyped] = useState("");
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Reset the typed value when the dialog closes so a prior confirmation
  // can't carry over to the next one. Clear any pending "Copied" timer on
  // unmount so it can't fire after the dialog is gone.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setTyped("");
      setCopied(false);
    }
  }
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const copyPhrase = async () => {
    if (!confirmPhrase) return;
    try {
      await navigator.clipboard.writeText(confirmPhrase);
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (permissions/insecure context) — no-op; the user can
      // still type the phrase manually.
    }
  };

  const phraseRequired = confirmPhrase !== undefined && confirmPhrase.length > 0;
  const phraseMatches = !phraseRequired || typed === confirmPhrase;
  const confirmDisabled = loading || !phraseMatches;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        {phraseRequired && (
          <div className="grid gap-2">
            <div className="flex flex-wrap items-center gap-1.5 text-sm font-normal text-muted-foreground">
              <Label htmlFor="confirm-phrase" className="font-normal">
                Type <span className="font-mono font-medium text-foreground">{confirmPhrase}</span> to
                confirm
              </Label>
              <button
                type="button"
                onClick={copyPhrase}
                className="inline-flex items-center gap-1 rounded px-1 text-xs text-muted-foreground hover:text-foreground"
                aria-label={copied ? "Copied" : "Copy name to clipboard"}
              >
                {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <Input
              id="confirm-phrase"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && phraseMatches && !loading) {
                  e.preventDefault();
                  onConfirm();
                }
              }}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            variant={variant}
            disabled={confirmDisabled}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {loading && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
